'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CTable from '../../components/shared-ui/CTable';
import {
  Button,
  Empty,
  Popconfirm,
  Space,
  TableProps,
  notification,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useConnection, useUser } from '@sezenta/connection';
import { DataEditModal, useDataEditModal } from '../../common/DataEditModal';
import CTag from '../../components/ui/CTag';
import {
  FilterFields,
  FilterPanel,
  FilterValues,
} from '../../common/antd-filter-panel';
import { FormSchema } from '@sezenta/antd-schema-form';
import { MasterData, MasterDataType, Permission, User } from '../../dto';
import { useParams } from 'react-router-dom';
import { useEventListener, useEvents } from '../../common/EventContext';
import { useBusinessContext } from '../../context/BusinessContext';
import MasterDataViewModal, {
  useMasterDataViewModal,
} from './MasterDataViewModal';

// CR004: per-contract master-data types that have cloneable defaults on the
// default contract. When a project has none of one of these, we offer a "Seed
// defaults" button that pulls the default contract's set in on demand.
const SEEDABLE_TYPES = ['CollectionType', 'Zone', 'SystemAdmin'];
// UI display overrides for master-data types whose enum value reads poorly to
// users. The backend `type` value (e.g. "SystemAdmin") and URL param are
// unchanged — only the label shown on screen differs.
const TYPE_DISPLAY: Record<
  string,
  { singular: string; plural: string; title: string }
> = {
  SystemAdmin: {
    singular: 'Notification Recipient',
    plural: 'notification recipients',
    title: 'Notification Recipients',
  },
};
// "CollectionType" -> "collection types", "SystemAdmin" -> "notification recipients".
const typeLabel = (type?: string) =>
  TYPE_DISPLAY[type ?? '']?.plural ??
  (type ?? '')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .toLowerCase() + 's';
// Title-case heading for the selected type: "CollectionType" -> "Collection
// Type", "SystemAdmin" -> "Notification Recipients".
const typeTitle = (type?: string) =>
  TYPE_DISPLAY[type ?? '']?.title ??
  (type ?? '').replace(/([A-Z])/g, ' $1').trim();
// Singular form for the "Add ..." button: enum types are already singular
// ("Zone", "Collection Type"); overridden types use their singular label.
const typeSingular = (type?: string) =>
  TYPE_DISPLAY[type ?? '']?.singular ??
  (type ?? '').replace(/([A-Z])/g, ' $1').trim();

const MasterDataPage = () => {
  const [data, setData] = useState<MasterData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [seeding, setSeeding] = useState<boolean>(false);
  const [value, setValue] = useState<FilterValues>([]);

  // server-side pagination + search
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const editModal = useDataEditModal();
  const connection = useConnection();
  const user = useUser();
  const viewModal = useMasterDataViewModal();
  const { type } = useParams<{ type: string }>();
  const events = useEvents();
  const { select, businessId, reload } = useBusinessContext();
  useEventListener(
    'MASTER_DATA',
    useCallback(
      (evt) => {
        if (!evt?.data?.type || evt.data.type !== type) return;
        setData((prev) => {
          if (evt.action === 'CREATE') {
            return Array.isArray(evt.data)
              ? [...evt.data, ...prev]
              : [evt.data, ...prev];
          } else if (evt.action === 'UPDATE') {
            return prev.map((item) =>
              item.id === evt.data.id ? evt.data : item,
            );
          } else if (evt.action === 'DELETE') {
            return prev.filter((item) => item.id !== evt.data.id);
          }
          return prev;
        });
      },
      [type],
    ),
  );

  // Filters are pushed to the server (so they apply across all pages, not just the
  // loaded one). Derived from the FilterPanel value.
  const cond = (name: string) =>
    value.find((c) => c.name === name && c.operator === 'eq')?.value as
      | string
      | undefined;
  const statusFilter = cond('status');
  const stateFilter = cond('state');
  const vtdFilter = cond('visibleToDriver');
  const defaultFilter = cond('default');

  const fetchData = useCallback(() => {
    setLoading(true);
    // Return the promise so callers (e.g. seed) can await the refresh.
    return (
      connection
        // CR004: scope per-contract types (Zone, …) to the active contract; the
        // server merges in shared rows (businessId IS NULL). Server-paginated +
        // searched + filtered.
        .get(`master-data/by-type/${type}`, {
          params: {
            businessId,
            page,
            limit: pageSize,
            search: debouncedSearch || undefined,
            status: statusFilter,
            state: stateFilter,
            visibleToDriver: vtdFilter,
            default: defaultFilter,
          },
        })
        .then((res) => {
          setData(res?.data?.data ?? []);
          setTotal(res?.data?.total ?? 0);
        })
        .finally(() => {
          setLoading(false);
        })
    );
  }, [
    connection,
    type,
    businessId,
    page,
    pageSize,
    debouncedSearch,
    statusFilter,
    stateFilter,
    vtdFilter,
    defaultFilter,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // reset to page 1 when the type or active contract changes
  useEffect(() => setPage(1), [type, businessId]);

  // debounce the search box → page 1
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const handleDelete = useCallback(
    async (record: any) => {
      try {
        await connection.delete(`master-data/${type}/${record.id}`);
        setData((prev) => prev.filter((item) => item.id !== record.id));
        notification.success({
          message: 'Deleted',
          description: 'Master data deleted successfully.',
        });
      } catch (error) {
        notification.error({
          message: 'Delete Failed',
          description: 'Unable to delete this entry.',
        });
      }
    },
    [connection, type],
  );

  const baseMasterDataSchema: FormSchema = useMemo(() => {
    return [
      {
        id: 'name',
        name: 'Name',
        type: 'string',
        props: {
          placeholder: 'Enter display name',
        },
        options: {
          rules: [{ required: true, message: 'Display Name is required.' }],
        },
      },
      {
        id: 'description',
        name: 'Description',
        type: 'string',
        props: {
          placeholder: 'Optional description',
        },
        options: {
          rules: [{ required: false }],
        },
      },
      {
        id: 'status',
        name: 'Status',
        type: 'select',
        props: {
          placeholder: 'Select status',
          options: [
            { label: 'Active', value: 'Active' },
            { label: 'Inactive', value: 'Inactive' },
          ],
        },
        options: {
          rules: [{ required: true, message: 'Status is required.' }],
        },
      },
    ];
  }, []);

  const systemAdminMasterDataSchema: FormSchema = useMemo(() => {
    return [
      {
        id: 'name',
        name: 'Name',
        type: 'string',
        props: {
          placeholder: 'Enter name',
        },
        options: {
          rules: [{ required: true, message: 'Name is required.' }],
        },
      },
      {
        id: 'email',
        name: 'Email',
        type: 'string',
        props: {
          placeholder: 'Enter email',
        },
        options: {
          rules: [
            { required: true, message: 'Email is required.' },
            { type: 'email', message: 'Enter a valid email address.' },
          ],
        },
      },
      {
        id: 'status',
        name: 'Status',
        type: 'select',
        props: {
          placeholder: 'Select status',
          options: [
            { label: 'Active', value: 'Active' },
            { label: 'Inactive', value: 'Inactive' },
          ],
        },
        options: {
          rules: [{ required: true, message: 'Status is required.' }],
        },
      },
    ];
  }, []);

  const collectionTypeUpdateSchema: FormSchema = useMemo(() => {
    return [
      ...baseMasterDataSchema,
      {
        id: 'weight',
        name: 'Weight',
        type: 'number',
        props: {
          placeholder: 'Enter weight',
        },
        options: {
          rules: [{ required: true, message: 'Weight is required' }],
        },
      },
      {
        id: 'state',
        name: 'State',
        type: 'select',
        props: {
          placeholder: 'Select state',
          options: select.states,
        },
        options: {
          rules: [{ required: true }],
        },
      },
      {
        id: 'visibleToDriver',
        name: 'Visible to Driver',
        type: 'switch2',
        props: {
          defaultChecked: false,
        },
      },
      {
        id: 'default',
        name: 'Default',
        type: 'switch2',
        props: {
          defaultChecked: false,
        },
      },
    ];
  }, [baseMasterDataSchema, select.states]);

  const collectionTypeCreateSchema: FormSchema = useMemo(() => {
    const createFields = [
      {
        id: 'weight',
        name: 'Weight',
        type: 'number',
        props: {
          placeholder: 'Enter weight',
        },
        options: {
          rules: [{ required: true, message: 'Weight is required' }],
        },
      },
      {
        id: 'visibleToDriver',
        name: 'Visible to Driver',
        type: 'switch2',
        props: {
          defaultChecked: false,
        },
      },
      {
        id: 'default',
        name: 'Default',
        type: 'switch2',
        props: {
          defaultChecked: false,
        },
      },
      {
        id: 'applyToAllStates',
        name: 'Apply to All States',
        type: 'switch2',
        props: {
          defaultChecked: true,
        },
      },
      {
        id: 'states',
        name: 'States',
        type: 'select',
        props: {
          placeholder: 'Select states',
          mode: 'multiple',
          options: select.states,
        },
        visible: (values: any) => !values.applyToAllStates,
        options: {
          rules: [
            { required: true, message: 'Please select at least one state' },
          ],
        },
      },
    ];
    return [...baseMasterDataSchema, ...createFields];
  }, [baseMasterDataSchema, select.states]);

  const getFormField = useCallback(
    (isEdit: boolean) => {
      switch (type) {
        case MasterDataType.COLLECTION_TYPE:
          if (isEdit) {
            return collectionTypeUpdateSchema;
          }
          return collectionTypeCreateSchema;
        case MasterDataType.SYSTEM_ADMIN:
          return systemAdminMasterDataSchema;
        default:
          return baseMasterDataSchema;
      }
    },
    [
      baseMasterDataSchema,
      collectionTypeCreateSchema,
      collectionTypeUpdateSchema,
      systemAdminMasterDataSchema,
      type,
    ],
  );
  const handleAdd = useCallback(() => {
    editModal.current
      ?.create(
        getFormField(false),
        `Create ${TYPE_DISPLAY[type ?? '']?.singular ?? type}`,
        'master-data',
        (formData) => {
          const base = {
            name: formData.name,
            status: formData.status,
            type,
            // CR004: stamp the active contract so per-contract types (Zone, …)
            // belong to this contract.
            businessId,
          };
          if (type === MasterDataType.COLLECTION_TYPE) {
            return {
              ...base,
              states: formData.states,
              weight: formData.weight,
              visibleToDriver: formData.visibleToDriver,
              default: formData.default,
            };
          }
          if (type === MasterDataType.SYSTEM_ADMIN) {
            return {
              ...base,
              email: formData.email,
            };
          }
          return {
            ...base,
            description: formData.description,
          };
        },
        undefined,
        {
          applyToAllStates: true,
        },
      )
      .then((d) => {
        console.log('d', d);
        fetchData();
        // Keep the business context (dropdowns in other screens) in sync.
        reload();
        notification.success({
          message: 'Created',
          description: 'Master data created successfully.',
        });
      })
      .catch((error) => {
        console.error(error);
        notification.error({
          message: 'Creation Failed',
          description: 'Something went wrong while creating master data.',
        });
      });
  }, [editModal, fetchData, getFormField, type, businessId, reload]);

  const filterSchema: FilterFields = useMemo(() => {
    const baseFilters: FilterFields = [
      {
        label: 'Status',
        name: 'status',
        operators: ['eq'],
        type: 'radio' as const,
        options: [
          { value: 'Active', label: 'Active' },
          { value: 'Inactive', label: 'Inactive' },
        ],
      },
    ];

    // Add additional filters for Collection Type
    if (type === 'CollectionType') {
      baseFilters.push(
        {
          label: 'State',
          name: 'state',
          operators: ['eq'],
          type: 'select' as const,
          options: select['states'],
          masterDataType: 'states' as const,
        },
        {
          label: 'Visible to Driver',
          name: 'visibleToDriver',
          operators: ['eq'],
          type: 'radio' as const,
          options: [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ],
        },
        {
          label: 'Default',
          name: 'default',
          operators: ['eq'],
          type: 'radio' as const,
          options: [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ],
        },
      );
    }

    return baseFilters;
  }, [type, select]);

  const columns: TableProps<any>['columns'] = useMemo(() => {
    const baseColumns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (text: string, record: User) => (
          <span
            className="text-teal-500 flex gap-2 items-center cursor-pointer hover:text-blue-500"
            onClick={() => viewModal.current?.view(record)}
          >
            {text}
          </span>
        ),
      },
      // {
      //   title: 'Type',
      //   dataIndex: 'type',
      //   key: 'type',
      // },
    ];

    const collectionColumns = [
      {
        title: 'Weight',
        dataIndex: 'weight',
        key: 'weight',
      },
      {
        title: 'State',
        dataIndex: 'state',
        key: 'state',
        render: (state: any, _: any) =>
          select.states.find((value) => value.value === state)?.label,
      },
      {
        title: 'Visible to Driver',
        dataIndex: 'visibleToDriver',
        key: 'visibleToDriver',
        render: (visibleToDriver: boolean) => {
          const isVisible = visibleToDriver ?? false;
          const color = isVisible ? 'green' : 'red';
          return (
            <CTag prefix="dot" color={color}>
              {isVisible ? 'YES' : 'NO'}
            </CTag>
          );
        },
      },
      {
        title: 'Default',
        dataIndex: 'default',
        key: 'default',
        render: (isDefault: boolean) => {
          const defaultValue = isDefault ?? false;
          const color = defaultValue ? 'green' : 'red';
          return (
            <CTag prefix="dot" color={color}>
              {defaultValue ? 'YES' : 'NO'}
            </CTag>
          );
        },
      },
    ];

    const systemAdminColumns = [
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
    ];

    const restColumns = [
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => {
          // Render an explicit label so a falsy/non-"Active" status (null, boolean
          // false, etc.) still shows "Inactive" text instead of an empty tag.
          const isActive = status === 'Active';
          return (
            <CTag prefix="dot" color={isActive ? 'green' : 'red'}>
              {isActive ? 'Active' : 'Inactive'}
            </CTag>
          );
        },
      },
      {
        title: 'Action',
        key: 'action',
        render: (_: any, record: any) => (
          <Space>
            {user?.permissions.includes(Permission.EDIT_MASTER_DATA) && (
              <Button
                icon={<EditOutlined />}
                type="text"
                className="text-[20px] text-gray-500"
                onClick={() =>
                  editModal.current?.edit(
                    record,
                    getFormField(true),
                    'Edit Master Data',
                    `master-data/${record.id}`,
                    (formData) => {
                      const base = {
                        name: formData.name,
                        status: formData.status,
                        type,
                      };
                      if (type === MasterDataType.COLLECTION_TYPE) {
                        return {
                          ...base,
                          state: formData.state,
                          weight: formData.weight,
                          visibleToDriver: formData.visibleToDriver,
                          default: formData.default,
                        };
                      }
                      if (type === MasterDataType.SYSTEM_ADMIN) {
                        return {
                          ...base,
                          email: formData.email,
                        };
                      }
                      return {
                        ...base,
                        description: formData.description,
                      };
                    },
                  )
                }
              />
            )}
            {/*{user?.permissions.includes(Permission.DELETE_SITE) && (*/}
            {/*  <Popconfirm*/}
            {/*    title="Are you sure you want to delete this entry?"*/}
            {/*    onConfirm={() => handleDelete(record)}*/}
            {/*  >*/}
            {/*    <Button*/}
            {/*      icon={<DeleteOutlined />}*/}
            {/*      type="text"*/}
            {/*      className="!text-[20px] !text-gray-500"*/}
            {/*    />*/}
            {/*  </Popconfirm>*/}
            {/*)}*/}
          </Space>
        ),
      },
    ];

    // Combine columns conditionally
    return type === MasterDataType.COLLECTION_TYPE
      ? [...baseColumns, ...collectionColumns, ...restColumns]
      : type === MasterDataType.SYSTEM_ADMIN
        ? [...baseColumns, ...systemAdminColumns, ...restColumns]
        : [...baseColumns, ...restColumns];
  }, [
    type,
    viewModal,
    select.states,
    user?.permissions,
    editModal,
    getFormField,
  ]);

  // CR004: clone the default contract's collection types into this project when it
  // has none of its own (same idea as "seed default roles").
  const seedDefaults = async () => {
    setSeeding(true);
    try {
      // Body must be an object, not `null` — the JSON body-parser runs in strict
      // mode and rejects a bare `null` payload with a 400. A generous timeout
      // covers a cold-start clone of many rows (the default timeout can fire while
      // the seed actually completes server-side → the "error but data on refresh").
      const res = await connection.post(
        `master-data/${type}/seed-defaults`,
        {},
        { params: { businessId }, timeout: 60000 },
      );
      notification.success({
        message: 'Seeded',
        description: `Added ${res.data?.created ?? 0} default item(s).`,
      });
    } catch {
      notification.error({
        message: 'Seed failed',
        description: 'Could not seed defaults.',
      });
    }
    // Refresh regardless of the request outcome: the seed may have completed on the
    // server even if the request errored/timed out. A refresh failure here must NOT
    // be reported as a seed failure. Seeding bypasses the per-item MASTER_DATA
    // events, so this explicit refresh (table + business context) is required.
    try {
      await fetchData();
    } catch {
      // non-fatal — the user can refresh manually
    }
    reload();
    setSeeding(false);
  };

  return (
    <div className="w-full h-full p-5 ctable">
      <div className="flex pl-3 pb-4 items-center">
        <div className="flex gap-3 items-center mr-auto">
          <div className="text-[24px] font-[600]">{typeTitle(type)}</div>
        </div>
      </div>
      <CTable
        loading={loading}
        title={typeTitle(type)}
        columns={columns}
        data={data}
        onSearch={setSearch}
        searchValue={search}
        emptyText={
          // Seedable per-contract type with a truly empty project (not a filtered
          // search): offer to import the default contract's set right here.
          // Gate on !loading so the seed CTA doesn't flash before the first page
          // of data arrives (total is 0 until the request resolves).
          !loading &&
          SEEDABLE_TYPES.includes(type ?? '') &&
          !search &&
          total === 0 ? (
            <div className="py-8 flex flex-col items-center gap-3">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span className="text-[#5E6870]">
                    No {typeLabel(type)} in this project yet
                  </span>
                }
              />
              <div className="text-[12px] text-[#84919A] max-w-[320px] text-center -mt-1">
                Import the default {typeLabel(type)}, or add your own with{' '}
                <span className="font-medium text-[#5E6870]">
                  Add {typeSingular(type)}
                </span>
                .
              </div>
              {user?.permissions.includes(Permission.CREATE_MASTER_DATA) && (
                <Button type="primary" loading={seeding} onClick={seedDefaults}>
                  Seed default {typeLabel(type)}
                </Button>
              )}
            </div>
          ) : undefined
        }
        pagination={{
          current: page,
          pageSize,
          total,
          pageSizeOptions: [10, 20, 50, 100],
          showSizeChanger: true,
          showTotal: (t) => `${t} entries`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        hideAddButton={
          user?.permissions.includes(Permission.CREATE_MASTER_DATA) === false
        }
        addBtnTitle={`Add ${typeSingular(type)}`}
        onAdd={handleAdd}
        filterComponent={
          <FilterPanel
            fields={filterSchema}
            value={value}
            onChange={(v) => {
              setValue(v ?? []);
              setPage(1);
            }}
          />
        }
      />
      <DataEditModal type="MASTER_DATA" ref={editModal} />
      <MasterDataViewModal ref={viewModal} />
    </div>
  );
};

export default MasterDataPage;
